import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from '../../hooks/useNavigate';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArticleIcon from '@mui/icons-material/Article';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PageHeader from '../components/shared/page-header';
import { useOfficialDailyDetailState } from './context/official-daily-detail-context';
import { useOfficialDailyDetailController } from './context/official-daily-detail-controller';

export function OfficialDailyDetailComponent() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { officialDailyDetail, loading, error } = useOfficialDailyDetailState();
    const { loadDetail } = useOfficialDailyDetailController();

    useEffect(() => {
        if (id) {
            loadDetail(parseInt(id));
        }
    }, [id]);

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const normalizeContent = (content?: string) => {
        if (!content) return '';

        // Remove caracteres de escape comuns
        let normalized = content
            .replace(/\\n/g, '\n')           // Converte \n literal em quebra de linha
            .replace(/\\t/g, ' ')            // Converte \t literal em espaço
            .replace(/\\r/g, '')             // Remove \r literal
            .replace(/\t/g, ' ')             // Converte tab em espaço
            .replace(/\r\n/g, '\n')          // Normaliza quebras de linha Windows
            .replace(/\r/g, '\n')            // Normaliza quebras de linha Mac
            .replace(/\n{3,}/g, '\n\n')      // Reduz múltiplas quebras de linha para no máximo 2
            .replace(/[ ]{2,}/g, ' ')        // Reduz múltiplos espaços para apenas 1
            .trim();                          // Remove espaços no início e fim

        return normalized;
    };

    const handleView = () => {
        if (officialDailyDetail?.fileViewUrl) {
            window.open(officialDailyDetail.fileViewUrl, '_blank');
        }
    };

    const handleDownload = () => {
        if (officialDailyDetail?.fileDownloadUrl) {
            window.open(officialDailyDetail.fileDownloadUrl, '_blank');
        }
    };

    if (loading) {
        return (
            <Box>
                <PageHeader
                    title="Edição do Diário Oficial"
                    subtitle="Carregando detalhes..."
                    breadcrumbs={[
                        { label: 'Diário Oficial', href: '/diario-oficial' },
                        { label: 'Detalhes' }
                    ]}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (error || !officialDailyDetail) {
        return (
            <Box>
                <PageHeader
                    title="Edição do Diário Oficial"
                    subtitle="Erro ao carregar detalhes"
                    breadcrumbs={[
                        { label: 'Diário Oficial', href: '/diario-oficial' },
                        { label: 'Detalhes' }
                    ]}
                />
                <Paper sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error || 'Não foi possível carregar os detalhes do diário oficial'}
                    </Alert>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/diario-oficial')}
                    >
                        Voltar
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title={`Edição do Diário Oficial - Edição ${officialDailyDetail.editionNumber}/${officialDailyDetail.year}`}
                subtitle={`Publicado em ${formatDate(officialDailyDetail.publicationDate)}`}
                breadcrumbs={[
                    { label: 'Diário Oficial', href: '/diario-oficial' },
                    { label: `Edição ${officialDailyDetail.editionNumber}` }
                ]}
            />

            {/* Cabeçalho com informações principais */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <ArticleIcon color="primary" fontSize="large" />
                            <Typography variant="h5" component="h1" fontWeight={600}>
                                Edição {officialDailyDetail.editionNumber} - Ano {officialDailyDetail.year}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip
                                icon={<CalendarTodayIcon />}
                                label={`Publicado em ${formatDate(officialDailyDetail.publicationDate)}`}
                                color="primary"
                                variant="outlined"
                            />
                            {officialDailyDetail.isClosed && (
                                <Chip
                                    icon={<VerifiedIcon />}
                                    label={`Encerrado em ${formatDate(officialDailyDetail.closedOn)}`}
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="text.secondary">
                                    Total de Páginas
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                    {officialDailyDetail.lastPage}
                                </Typography>
                            </Grid>
                            {officialDailyDetail.authenticityCode && (
                                <Grid item xs={6} sm={8}>
                                    <Typography variant="caption" color="text.secondary">
                                        Código de Autenticidade
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                        {officialDailyDetail.authenticityCode}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/diario-oficial')}
                                fullWidth
                            >
                                Voltar
                            </Button>
                            {officialDailyDetail.fileViewUrl && (
                                <Button
                                    variant="outlined"
                                    startIcon={<DescriptionIcon />}
                                    onClick={handleView}
                                    fullWidth
                                >
                                    Visualizar Diário Oficial
                                </Button>
                            )}
                            {officialDailyDetail.fileDownloadUrl && (
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                    fullWidth
                                >
                                    Baixar Diário Oficial
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Lista de publicações */}
            {officialDailyDetail.publications && officialDailyDetail.publications.length > 0 && (
                <Paper sx={{ p: 3 }} variant="outlined">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <DescriptionIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                            Publicações ({officialDailyDetail.publications.length})
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {officialDailyDetail.publications.map((publication, index) => (
                            <Accordion key={publication.id || index}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        borderLeft: 3,
                                        borderColor: 'primary.main'
                                    }}
                                >
                                    <Box sx={{ width: '100%', pr: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                                                {publication.title || 'Publicação sem título'}
                                            </Typography>
                                            {publication.isRepublication && (
                                                <Chip label="Republicação" size="small" color="warning" />
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                            {publication.category && (
                                                <Chip
                                                    icon={publication.categoryIcon ? <CategoryIcon /> : undefined}
                                                    label={publication.category}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                            {publication.subCategory && (
                                                <Chip
                                                    label={publication.subCategory}
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                />
                                            )}
                                            {publication.numberingCode && (
                                                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                                    Nº {publication.numberingCode}
                                                </Typography>
                                            )}
                                            {(publication.initialPage && publication.finalPage) && (
                                                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                                    • Pág. {publication.initialPage === publication.finalPage
                                                        ? publication.initialPage
                                                        : `${publication.initialPage}-${publication.finalPage}`}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {publication.observation && (
                                            <Box>
                                                <Typography variant="subtitle2" color="brand.text" gutterBottom>
                                                    Observação:
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {normalizeContent(publication.observation)}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Divider />

                                        <Grid container spacing={2}>
                                            {publication.publishingVehicle && (
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <NewspaperIcon fontSize="small" color="action" />
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Veículo de Publicação
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {publication.publishingVehicle}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {publication.publishingAgency && (
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <BusinessIcon fontSize="small" color="action" />
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Órgão Publicador
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {publication.publishingAgency}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {publication.publicationDate && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Data de Publicação
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatDate(publication.publicationDate)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {publication.sanctionDate && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Data de Sanção
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatDate(publication.sanctionDate)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {publication.responsiblePresident && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Presidente Responsável
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {publication.responsiblePresident}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {publication.tenderCommission && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Comissão de Licitação
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {publication.tenderCommission}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(publication.initialPage || publication.finalPage) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Páginas no Diário
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {publication.initialPage === publication.finalPage
                                                            ? `Página ${publication.initialPage}`
                                                            : `Páginas ${publication.initialPage} a ${publication.finalPage}`
                                                        }
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {publication.authenticityCode && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Código de Autenticidade
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {publication.authenticityCode}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>

                                        {(publication.fileViewUrl || publication.fileDownloadUrl) && (
                                            <>
                                                <Divider />
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                    {publication.fileViewUrl && (
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<DescriptionIcon />}
                                                            onClick={() => window.open(publication.fileViewUrl, '_blank')}
                                                        >
                                                            Visualizar Publicação
                                                        </Button>
                                                    )}
                                                    {publication.fileDownloadUrl && (
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<DownloadIcon />}
                                                            onClick={() => window.open(publication.fileDownloadUrl, '_blank')}
                                                        >
                                                            Baixar Publicação
                                                        </Button>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Paper>
            )}

            {(!officialDailyDetail.publications || officialDailyDetail.publications.length === 0) && (
                <Paper sx={{ p: 3 }} variant="outlined">
                    <Alert severity="info">
                        Este diário oficial não possui publicações cadastradas.
                    </Alert>
                </Paper>
            )}
        </Box>
    );
}
